'use client';

import { Box, Heading, Input, Text, Stack, CloseButton, Flex } from '@skin-seoul/design-system';
import React, { useEffect, useCallback, useState, useRef } from 'react';
import { SafeLink } from '@/components/ui/SafeLink';
import type { CategoryItem } from '@skin-seoul/service';
import { contourItems, mensItems, moisturizerItems, cleanserItems, maskItems, sunCareItems, CLASSNAME } from '@skin-seoul/service';
import { brandWithImage } from '@skin-seoul/service';
import CategorySection from './sections/CategorySection';
import BrandSection from './sections/BrandSection';
import LucideIcon from '@/components/ui/LucideIcon';
import { FiboSearchModal } from '@/components/fibosearch/FiboSearchModal';
import { overlay } from 'overlay-kit';
import { AnimatePresence, motion } from 'motion/react';
const getActiveItems = (activeCategory: string): CategoryItem[] => {
  switch (activeCategory) {
    case 'Contour':
      return contourItems;
    case "Men's":
      return mensItems;
    case 'Moisturizer':
      return moisturizerItems;
    case 'Cleanser':
      return cleanserItems;
    case 'Masks':
      return maskItems;
    case 'Sun Care':
      return sunCareItems;
    case 'Brands':
      return brandWithImage;
    default:
      return moisturizerItems;
  }
};
const MotionFlex = motion.create(Flex);
const categories = ['Contour', "Men's", 'Moisturizer', 'Cleanser', 'Masks', 'Sun Care', 'Brands', 'Shop All'];
const getCategoryCollectionUrl = (categoryName: string): string => {
  switch (categoryName) {
    case 'Contour':
      return '/collections/contour/';
    case "Men's":
      return '/collections/mens/';
    case 'Moisturizer':
      return '/collections/moisturizer/';
    case 'Cleanser':
      return '/collections/cleanser/';
    case 'Masks':
      return '/collections/mask-pack/';
    case 'Sun Care':
      return '/collections/sun-care/';
    default:
      return '/shop/';
  }
};
interface CategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const CATEGORIES_MODAL_ID = 'CATEGORIES_MODAL';
const ProductCategoryModal = ({
  onClose,
  isOpen
}: CategoriesModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Contour');
  const modalRef = useRef<HTMLDivElement>(null);
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);
  const handleCategoryClick = useCallback((category: string) => {
    if (category !== 'Shop All') {
      setActiveCategory(category);
    }
  }, []);
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Trap focus within modal when open
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Focus the first focusable element in the modal
      const focusableElements = modalRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }
  }, [isOpen]);
  return <AnimatePresence mode="wait" data-sentry-element="AnimatePresence" data-sentry-component="ProductCategoryModal" data-sentry-source-file="ProductCategoryModal.tsx">
      {isOpen && <MotionFlex ref={modalRef} backgroundColor="generic.0" initial={{
      opacity: 0
    }} animate={{
      opacity: 1,
      transition: {
        duration: 0.2
      },
      transitionEnd: {
        opacity: 1
      }
    }} exit={{
      opacity: 0
    }} position="fixed" bottom="0" left="0" right="0" zIndex="sticky" flexDirection="column" height="100%" width="100%" boxSizing="border-box" px="10px">
          <Flex height="50px" pt="20px" pb="10px" borderBottomWidth="1px" alignItems="center" justifyContent="center" borderColor="neutral.100">
            <Heading as="h2" textStyle="heading.h6" fontWeight="medium" color="neutral.900">
              CATEGORIES
            </Heading>

            <CloseButton position="absolute" right="16px" top="14px" size="md" color="neutral.500" aria-label="Close" focusRing="none" onClick={handleClose} />
          </Flex>

          <Box py="10px">
            <Box position="relative" onClick={() => {
          overlay.open(({
            isOpen,
            close,
            unmount
          }) => <FiboSearchModal isOpen={isOpen} onClose={close} onExit={unmount} />, {
            overlayId: 'search-modal'
          });
        }}>
              <Input placeholder="Find your products" _placeholder={{
            color: 'neutral.400'
          }} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} borderRadius={0} readOnly border="none" borderBottom="2px solid" borderBottomColor="neutral.100/50" pr="40px" />
              <Box position="absolute" right="12px" top="50%" transform="translateY(-50%)" pointerEvents="none">
                <LucideIcon name="Search" boxSize="20px" color="brand.400" strokeWidth={1} />
              </Box>
            </Box>
          </Box>

          <Flex height="100%" overflow="hidden">
            <Stack width="30%" alignItems="flex-start" gap={0} bg="neutral.50" height="100%" overflowY="auto" className={CLASSNAME.NOT_TRANSLATE}>
              {categories.map(category => {
            if (category === 'Shop All') {
              return <SafeLink key={category} href="/shop/" unstyled onClick={onClose}>
                      <Box py={3} px={2} width="100%" cursor="pointer" bg="transparent">
                        <Text textStyle="paragraph.small" whiteSpace="nowrap" color="neutral.600" fontWeight="medium" className={CLASSNAME.NOT_TRANSLATE}>
                          {category}
                        </Text>
                      </Box>
                    </SafeLink>;
            }
            const isActive = activeCategory === category;
            return <Box key={category} paddingY={3} paddingX={2} width="100%" cursor="pointer" onClick={() => handleCategoryClick(category)} backgroundColor={isActive ? 'generic.0' : 'transparent'}>
                    <Text textStyle="paragraph.small" whiteSpace="nowrap" color="neutral.600" fontWeight="medium" className={CLASSNAME.NOT_TRANSLATE}>
                      {category}
                    </Text>
                  </Box>;
          })}
            </Stack>

            <Box width="70%" height="100%" overflowY="scroll" px="10px" pb="63px">
              {(() => {
            switch (activeCategory) {
              case 'Brands':
                return <BrandSection items={getActiveItems(activeCategory)} />;
              default:
                return <CategorySection items={getActiveItems(activeCategory)} categoryName={activeCategory} categoryUrl={getCategoryCollectionUrl(activeCategory)} />;
            }
          })()}
            </Box>
          </Flex>
        </MotionFlex>}
    </AnimatePresence>;
};
export default ProductCategoryModal;